/* Circadia consent config — THE single source of truth for cookie/analytics consent.
   ============================================================================
   Client-Web Analytics + Law-25 Consent (S1).

   This file is the ONE config point for the consent policy. Both the cookie
   banner (consent-banner.js) and the analytics harness (analytics-emit.js) read
   their consent state through window.Circadia.consent defined here — so the
   HARD GATE (no analytics event fires, no visitor id is minted, without analytics
   consent) is enforced in exactly one place.

   ── THE GEO-GATED POLICY (P1-24) ─────────────────────────────────────────────
   POLICY decides the DEFAULT analytics-consent state BEFORE the visitor makes an
   explicit choice. It is derived PER-VISITOR from the country injected by
   theme.liquid (window.Circadia.geoCountry = Shopify's localization.country, set
   BEFORE this file runs):
     country 'CA'     → 'law25'  — strict explicit opt-in / default-deny (Quebec
                        Law-25 posture). Canada is gated at COUNTRY level because no
                        reliable province signal exists client-side or via Liquid
                        (timezone can't isolate Quebec — America/Toronto covers both
                        QC and ON; there is no province in localization/request).
                        Applying Law-25's default-deny to all of Canada is
                        stricter-than-required outside Quebec but compliant everywhere
                        in CA.
     any other known  → 'pipeda' — implied consent permissible: analytics defaults ON
                        (events flow + visitor id mints) until the visitor refuses.
                        (Unchanged until counsel answers LEGAL_ACTION_PLAN A1: REL-SH-1 step 2.)
     absent / unknown → 'law25'  — REL-SH-1 step 1 (2026-10-09). When no country is available the
                        visitor is NOT assumed to be somewhere implied consent is allowed: the
                        default is default-deny (opt-in), which LEGAL_ACTION_PLAN A1 says Path 1 must
                        do when the geography is uncertain, and which is right under either answer.
                        "Known" means a two-letter upper-case ISO code other than the user-assigned
                        'XX'/'ZZ'; null, '', a number or anything else is unknown.
   (marketing/preferences are NEVER implied — only analytics, and only under PIPEDA.)
   An IMPLIED analytics grant (the first-visit X/Escape close under PIPEDA) is stored with
   implied:true and is honoured only while the policy is still 'pipeda'; once the same browser
   resolves to a strict policy it is ignored and the visitor is asked again.
   The banner, harness, and mint-gate all honour POLICY automatically.

   The server stays policy-AGNOSTIC: the event bridge INDEPENDENTLY hard-gates on the
   consent snapshot this file produces (analytics events flow only when the snapshot
   reports analytics:true; missing/false ⇒ fail-closed). So a stricter client default
   (law25 for CA) only means the snapshot reports analytics:false by default there — it
   can never WEAKEN the server gate. */

(function () {
  'use strict';

  // ============ THE GEO-GATED CONSENT POLICY (P1-24) ============
  // Per-visitor, from the injected country: 'CA' → strict Law-25 (default-deny);
  // an absent or unknown country → strict too (REL-SH-1 step 1); any other KNOWN
  // country → PIPEDA (analytics implied-on, until counsel answers A1).
  // geoCountry is set by theme.liquid BEFORE this file loads.
  function resolvePolicy() {
    var geo = (window.Circadia && window.Circadia.geoCountry) || null;
    var known = typeof geo === 'string' && /^[A-Z]{2}$/.test(geo) && geo !== 'XX' && geo !== 'ZZ';
    if (!known) return 'law25';
    return geo === 'CA' ? 'law25' : 'pipeda';
  }
  var POLICY = resolvePolicy();
  // Bump to invalidate ALL stored consent + re-prompt every visitor (e.g. when the
  // banner copy or the category set materially changes — a policy-version bump).
  // Bumped for P1-24 so every visitor re-collects consent under the geo-gated policy
  // (clears any pre-fix implied-analytics grant made by a CA visitor under the old
  // PIPEDA-everywhere default).
  // Bumped again 2026-07-29: the em-dash sweep (87d5fea, 2026-07-27) changed the
  // banner disclosure copy, including the string sent as consent_text_shown,
  // without bumping this. Two different disclosure texts existed under
  // '2026-07-1'. No consent_records data was migrated: each row stores its own
  // consent_text_shown verbatim, so what every visitor actually saw is
  // preserved, and rewriting a Law 25 audit row's version label after the fact
  // would destroy the "what we recorded at the time" property that makes the
  // trail evidentiary.
  // Bumped again 2026-10-09 (REL-SH-1 step 1): the strict policy now covers an
  // absent/unknown country, and an implied analytics grant is now stored with
  // implied:true. A grant stored before this carries NO marker, so it cannot be told
  // from an explicit one; bumping re-asks everyone once, which is the only safe reading.
  // ROLLBACK HAZARD: rolling the asset files back would make cookies of the old version
  // valid again (their implied grants unmarked), so a rollback must keep this bump.
  var POLICY_VERSION = '2026-10-1';
  // =============================================================

  var COOKIE = 'circadia_consent';
  var MAX_AGE_SECONDS = 60 * 60 * 24 * 365; // ~12 months, then re-prompt

  // Only analytics can be implied (PIPEDA). marketing/preferences always default
  // off until an explicit grant.
  function policyDefault(category) {
    if (category === 'analytics') return POLICY === 'pipeda';
    return false;
  }

  function readCookie() {
    try {
      var m = document.cookie.match(/(?:^|;\s*)circadia_consent=([^;]+)/);
      if (!m) return null;
      var obj = JSON.parse(decodeURIComponent(m[1]));
      if (!obj || typeof obj !== 'object') return null;
      // A version bump invalidates the stored choice → treat as no-choice → re-prompt.
      if (obj.v !== POLICY_VERSION) return null;
      // An IMPLIED analytics grant (the PIPEDA X/Escape close) is honoured only where
      // implying one is allowed. Under a strict policy it is ignored: no-choice again.
      if (obj.implied === true && obj.analytics === true && POLICY !== 'pipeda') return null;
      return obj;
    } catch (_) {
      return null;
    }
  }

  // opts.implied === true marks a choice the visitor did not make with a button (the
  // first-visit X/Escape close): it is stored as implied:true so readCookie can refuse
  // to honour an implied GRANT once the policy is strict. An explicit choice has no marker.
  function writeCookie(choice, opts) {
    try {
      var payload = {
        v: POLICY_VERSION,
        analytics: choice.analytics === true,
        marketing: choice.marketing === true,
        preferences: choice.preferences === true,
        ts: Date.now(),
      };
      if (opts && opts.implied === true) payload.implied = true;
      var enc = encodeURIComponent(JSON.stringify(payload));
      var secure =
        window.location && window.location.protocol === 'https:' ? '; Secure' : '';
      document.cookie =
        COOKIE + '=' + enc + '; Max-Age=' + MAX_AGE_SECONDS + '; Path=/; SameSite=Lax' + secure;
      return payload;
    } catch (_) {
      return null;
    }
  }

  // The EFFECTIVE per-category consent: the explicit (current-version) cookie
  // choice if present, else the policy default.
  function snapshot() {
    var c = readCookie();
    if (c) {
      return {
        analytics: c.analytics === true,
        marketing: c.marketing === true,
        preferences: c.preferences === true,
      };
    }
    return {
      analytics: policyDefault('analytics'),
      marketing: policyDefault('marketing'),
      preferences: policyDefault('preferences'),
    };
  }

  function analyticsGranted() {
    return snapshot().analytics === true;
  }

  // Has the visitor made an explicit, current-version choice? (Drives whether the
  // banner re-prompts.)
  function hasExplicitChoice() {
    return readCookie() !== null;
  }

  if (!window.Circadia) window.Circadia = {};
  window.Circadia.consent = {
    POLICY: POLICY,
    POLICY_VERSION: POLICY_VERSION,
    COOKIE: COOKIE,
    policyDefault: policyDefault,
    snapshot: snapshot,
    analyticsGranted: analyticsGranted,
    hasExplicitChoice: hasExplicitChoice,
    readCookie: readCookie,
    writeCookie: writeCookie,
  };
})();
